/**
 * 样式相关工具函数
 */

/**
 * 将十六进制颜色转换为rgba格式
 * @param {String} hex - 十六进制颜色
 * @param {Number} alpha - 透明度 (0-1)
 * @returns {String} rgba颜色字符串
 */
export function hexToRgba(hex, alpha = 1) {
  if (!hex) return hex
  
  // 移除 # 号
  hex = hex.replace('#', '')
  
  // 处理简写形式 (#fff)
  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('')
  }
  
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/**
 * 生成随机颜色
 * @returns {String} 十六进制颜色
 */
export function generateRandomColor() {
  const letters = '0123456789ABCDEF'
  let color = '#'
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)]
  }
  return color
}

/**
 * 生成颜色调色板
 * @param {Number} count - 颜色数量
 * @param {String} baseColor - 基础颜色
 * @returns {Array} 颜色数组
 */
export function generateColorPalette(count, baseColor = '#1890ff') {
  const colors = []
  const base = baseColor.replace('#', '')
  
  for (let i = 0; i < count; i++) {
    const hue = (360 / count) * i
    colors.push(`hsl(${hue}, 70%, 50%)`)
  }
  
  return colors
}

/**
 * 计算对比度
 * @param {String} color1 - 颜色1
 * @param {String} color2 - 颜色2
 * @returns {Number} 对比度值
 */
export function calculateContrast(color1, color2) {
  const getLuminance = (color) => {
    const rgb = hexToRgb(color)
    const [r, g, b] = rgb.map(c => {
      c = c / 255
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
  }
  
  const lum1 = getLuminance(color1)
  const lum2 = getLuminance(color2)
  const brightest = Math.max(lum1, lum2)
  const darkest = Math.min(lum1, lum2)
  
  return (brightest + 0.05) / (darkest + 0.05)
}

/**
 * 十六进制转RGB
 * @param {String} hex - 十六进制颜色
 * @returns {Array} RGB数组
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  return result ? [
    parseInt(result[1], 16),
    parseInt(result[2], 16),
    parseInt(result[3], 16)
  ] : [0, 0, 0]
}

/**
 * 生成CSS变量
 * @param {Object} variables - 变量对象
 * @returns {String} CSS字符串
 */
export function generateCSSVariables(variables) {
  let css = ':root {\n'
  for (const [key, value] of Object.entries(variables)) {
    css += `  --${key}: ${value};\n`
  }
  css += '}'
  return css
}

/**
 * 合并样式对象
 * @param {Object} styles - 样式对象数组
 * @returns {Object} 合并后的样式对象
 */
export function mergeStyles(...styles) {
  return styles.reduce((acc, style) => {
    if (style && typeof style === 'object') {
      return { ...acc, ...style }
    }
    return acc
  }, {})
}
